import type { Action, DrawReason, GameEvent, Outcome } from './actions.js';
import type { Dictionary } from './dictionary.js';
import { applyMoves, canPlaceAll, extractRuns } from './grid.js';
import { randomSeed } from './rng.js';
import { buildBag, faceOf, requiredSets, roundsAvailable } from './tiles.js';
import { canDeclare, validateGrid } from './validate.js';
import { scoreSeat, type ScoreBreakdown } from './scoring.js';
import { BRAND } from './branding.js';
import {
  DEFAULT_CONFIG, key, keyOf, parseKey,
  type Coord, type CoordKey, type LogEntry, type MatchConfig, type MatchState, type Player,
  type Seat, type Tile,
} from './types.js';

export const MAX_SEATS = 20;
export const SEAT_COLOURS = [
  '#f2545b', '#f2a65a', '#f2d64b', '#8cc63f', '#3fbf7f', '#3fb8c6',
  '#4a8ef2', '#6f5cf2', '#a95cf2', '#e05cc6', '#f27ba3', '#c68b5c',
  '#7f8c8d', '#2ecc71', '#e67e22', '#16a085', '#9b59b6', '#34495e',
  '#d35400', '#27ae60',
];

export function createMatch(matchId: string, config?: Partial<MatchConfig>): MatchState {
  const merged = { ...DEFAULT_CONFIG, ...config };
  // A match with no seed of its own would be dealt the same bag as every other
  // match ever played. Tests pass an explicit seed and stay reproducible.
  if (!merged.seed) merged.seed = randomSeed();
  return {
    matchId,
    phase: 'lobby',
    config: merged,
    seats: new Map(),
    players: new Map(),
    bag: [],
    leftover: [],
    roundsTaken: 0,
    startedAt: null,
    lastDrawAt: 0,
    finishedAt: null,
    winnerSeatId: null,
    wentOutSeatId: null,
    log: [],
    version: 0,
    seatSeq: 0,
  };
}

const fail = (error: string): Outcome => ({ ok: false, error, events: [] });
const done = (...events: GameEvent[]): Outcome => ({ ok: true, events });

function note(s: MatchState, entry: Omit<LogEntry, 'at'>, now: number): void {
  s.log.push({ at: now, ...entry });
  if (s.log.length > 400) s.log.splice(0, s.log.length - 400);
}

function newSeat(s: MatchState, name: string): Seat {
  // Never derived from `seats.size`: leaving a shared seat deletes the empty
  // one behind you, and the next id minted from the count would collide with a
  // seat somebody else is sitting in. `?? seats.size` recovers the counter for
  // any match persisted before it existed.
  s.seatSeq = (s.seatSeq ?? s.seats.size) + 1;
  const id = `s${s.seatSeq}`;
  const seat: Seat = {
    id,
    name,
    colour: SEAT_COLOURS[s.seats.size % SEAT_COLOURS.length] as string,
    members: [],
    rack: [],
    grid: new Map(),
    peeksRemaining: 0,
    lockedUntil: 0,
    lastPlacementAt: 0,
    finishedAt: null,
    connectedMembers: 0,
    stuck: false,
  };
  s.seats.set(id, seat);
  return seat;
}

/** Seats that still hold tiles and have not conceded. */
export function activeSeats(s: MatchState): Seat[] {
  return [...s.seats.values()].filter((seat) => seat.finishedAt === null);
}

/** Tiles needed to serve every active seat one more round. */
export function drawNeeded(s: MatchState): number {
  return activeSeats(s).length * s.config.drawPerRound;
}

/** Draw rounds the current bag can still fund. */
export function roundsRemaining(s: MatchState): number {
  const need = drawNeeded(s);
  return need === 0 ? 0 : Math.floor(s.bag.length / need);
}

/** Seats that have said they are stuck, and how many are needed. */
export function stuckTally(s: MatchState): { count: number; needed: number } {
  const active = activeSeats(s);
  return { count: active.filter((x) => x.stuck).length, needed: active.length };
}

/** Milliseconds until the clock forces a draw, or null when it is switched off. */
export function msUntilForcedDraw(s: MatchState, now: number): number | null {
  if (s.config.forceDrawMs <= 0 || s.phase !== 'playing') return null;
  return Math.max(0, s.lastDrawAt + s.config.forceDrawMs - now);
}

/**
 * Tiles this seat is still owed for the rest of the match.
 *
 * What a player actually wants to know when deciding how much room to leave:
 * not how big the bag is, but how many more letters are coming to them.
 */
export function tilesToCome(s: MatchState): number {
  return roundsRemaining(s) * s.config.drawPerRound;
}

/**
 * Deal a round to every active seat.
 *
 * The single path by which tiles leave the bag during play, whether the draw
 * was earned by a call, agreed by a stuck table, or forced by the clock. They
 * differ only in what the client is told to shout.
 */
function drawRound(
  s: MatchState, reason: DrawReason, seatId: string | null, now: number,
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const seat of activeSeats(s)) {
    const drawn = s.bag.splice(0, s.config.drawPerRound);
    seat.rack.push(...drawn);
    seat.stuck = false;                     // a fresh hand is a fresh start
    if (s.config.peek.mode === 'limited') {
      seat.peeksRemaining += s.config.peek.budgetPerRound;
    }
    events.push({ e: 'drew', seatId: seat.id, tileIds: drawn.map((t) => t.id) });
  }
  s.roundsTaken++;
  s.lastDrawAt = now;
  const remaining = roundsRemaining(s);
  if (remaining <= 1) events.push({ e: 'bagLow', remaining: s.bag.length });
  return [
    { e: 'drawAll', reason, seatId, round: s.roundsTaken, drawn: s.config.drawPerRound },
    ...events,
  ];
}

/**
 * Apply one action. Mutates `state` in place and returns what happened.
 *
 * The same call runs on the client for instant feedback and again on the
 * server as the authority — so a client that lies about a word simply has its
 * declaration refused when the server repeats the check against its own bag,
 * rack and dictionary.
 */
export function apply(
  state: MatchState,
  action: Action,
  dict: Dictionary,
  now: number,
): Outcome {
  const before = state.version;
  fillMissingSettings(state.config);
  const wasFinished = state.phase === 'finished';
  const out = applyInner(state, action, dict, now);
  // Every way a match can end passes through here, so this is where it is
  // added to the table's running totals — exactly once per match.
  if (!wasFinished && state.phase === 'finished') tallySeries(state, dict);
  // A clock tick that changed nothing must not bump the version. Consumers
  // treat a version change as "something happened and the view needs
  // rebuilding", and a once-a-second false positive rebuilds the lobby out
  // from under the player's finger.
  const idle = action.t === 'tick' && out.events.length === 0;
  if (out.ok && !idle && state.version === before) state.version++;
  return out;
}

function applyInner(s: MatchState, a: Action, dict: Dictionary, now: number): Outcome {
  switch (a.t) {
    // -----------------------------------------------------------------
    case 'join': {
      if (s.players.has(a.playerId)) {
        const p = s.players.get(a.playerId) as Player;
        p.connected = true;
        p.disconnectedAt = null;
        recount(s);
        return done({ e: 'lobby', reason: 'reconnected' });
      }
      // A host who has been gone a long while can be replaced by the next
      // arrival; a host who stepped away for a minute cannot.
      reclaimHostIfAbandoned(s, now);
      const spectator = a.asSpectator === true || s.phase !== 'lobby';
      let seatId = '';
      if (!spectator) {
        if (s.seats.size >= MAX_SEATS) return fail('This table is full (20 seats).');
        seatId = newSeat(s, a.name).id;
      }
      const isHost = ![...s.players.values()].some((q) => q.isHost);
      const player: Player = {
        id: a.playerId, name: a.name, seatId, isHost,
        connected: true, isSpectator: spectator,
      };
      s.players.set(a.playerId, player);
      if (seatId) (s.seats.get(seatId) as Seat).members.push(a.playerId);
      recount(s);
      note(s, {
        kind: 'joined', playerId: a.playerId,
        text: `${a.name} joined${spectator ? ' as a spectator' : ''}`,
      }, now);
      return done({ e: 'lobby', reason: 'joined' });
    }

    /*
     * A dropped connection.
     *
     * It used to release the seat in the lobby, and pass on the host's role.
     * But the commonest reason for a phone to drop its connection in the lobby
     * is the host switching away to share the invite — so the table emptied,
     * the first guest to follow the link became host, and the real host came
     * back as a stranger. Now the seat and the role are simply held. Somebody
     * who is still gone when the match starts is left out of it (see
     * `start`), and a host gone for longer than HOST_GRACE_MS can be replaced.
     */
    case 'leave': {
      const p = s.players.get(a.playerId);
      if (!p) return fail('Unknown player.');
      p.connected = false;
      p.disconnectedAt = now;
      recount(s);
      return done({ e: 'lobby', reason: 'disconnected' });
    }

    /** A deliberate exit: the seat goes, and so does hosting. */
    case 'depart': {
      const p = s.players.get(a.playerId);
      if (!p) return fail('Unknown player.');
      p.connected = false;
      p.disconnectedAt = now;
      if (s.phase === 'lobby') {
        dropPlayer(s, p);
      } else if (p.isHost) {
        // Mid-match the seat is kept — the grid is still scored — but somebody
        // present has to be able to end or restart it.
        p.isHost = false;
        promoteHost(s);
      }
      recount(s);
      note(s, { kind: 'left', playerId: a.playerId, text: `${p.name} left` }, now);
      return done({ e: 'lobby', reason: 'left' });
    }

    case 'rename': {
      const p = s.players.get(a.playerId);
      if (!p) return fail('Unknown player.');
      p.name = a.name.slice(0, 24);
      const seat = s.seats.get(p.seatId);
      if (seat && seat.members.length === 1) seat.name = p.name;
      return done();
    }

    case 'newSeat': {
      if (s.phase !== 'lobby') return fail('Seats are fixed once the match starts.');
      const p = s.players.get(a.playerId);
      if (!p) return fail('Unknown player.');
      if (s.seats.size >= MAX_SEATS) return fail('This table is full (20 seats).');
      detach(s, p);
      const seat = newSeat(s, a.name ?? p.name);
      seat.members.push(p.id);
      p.seatId = seat.id;
      p.isSpectator = false;
      recount(s);
      return done({ e: 'lobby', reason: 'seat' });
    }

    /**
     * Joining an occupied seat is how a team forms: two people, one rack, one
     * grid. That is the whole co-operative mode — a child and an adult share a
     * hand and think out loud together while the table stays fair, because the
     * bag is divided per seat rather than per person.
     */
    case 'takeSeat': {
      if (s.phase !== 'lobby') return fail('Seats are fixed once the match starts.');
      const p = s.players.get(a.playerId);
      const seat = s.seats.get(a.seatId);
      if (!p) return fail('Unknown player.');
      if (!seat) return fail('That seat no longer exists.');
      if (seat.members.includes(p.id)) return done();
      detach(s, p);
      seat.members.push(p.id);
      p.seatId = seat.id;
      p.isSpectator = false;
      if (seat.members.length > 1 && !seat.name.includes('+')) {
        seat.name = seat.members
          .map((m) => s.players.get(m)?.name ?? '?')
          .join(' + ')
          .slice(0, 32);
      }
      recount(s);
      note(s, { kind: 'seated', playerId: p.id, seatId: seat.id,
        text: `${p.name} teamed up on ${seat.name}` }, now);
      return done({ e: 'lobby', reason: 'team' });
    }

    case 'renameSeat': {
      const seat = s.seats.get(a.seatId);
      const p = s.players.get(a.playerId);
      if (!seat || !p) return fail('Unknown seat.');
      if (!seat.members.includes(p.id) && !p.isHost) return fail('Not your seat.');
      seat.name = a.name.slice(0, 32);
      return done();
    }

    case 'configure': {
      const p = s.players.get(a.playerId);
      if (!p?.isHost) return fail('Only the host can change the settings.');
      if (s.phase !== 'lobby') return fail('Settings are locked once the match starts.');
      const next = { ...s.config, ...a.config };
      fillMissingSettings(next);
      const problem = checkConfig(next, Math.max(1, s.seats.size));
      if (problem) return fail(problem);
      s.config = next;
      note(s, { kind: 'config', playerId: p.id, text: `${p.name} changed the settings` }, now);
      return done({ e: 'lobby', reason: 'config' });
    }

    case 'start': {
      reclaimHostIfAbandoned(s, now, a.playerId);
      const p = s.players.get(a.playerId);
      if (!p?.isHost) return fail('Only the host can start the match.');
      // Anyone whose connection is still down when the match begins is left
      // out of it, rather than being dealt a rack nobody will ever play.
      for (const q of [...s.players.values()]) {
        if (!q.connected && q.id !== p.id) dropPlayer(s, q);
      }
      if (s.phase !== 'lobby') return fail('The match has already started.');
      const seats = [...s.seats.values()];
      if (seats.length === 0) return fail('Nobody is seated.');
      const problem = checkConfig(s.config, seats.length);
      if (problem) return fail(problem);

      const build = buildBag(seats.length, s.config);
      s.bag = build.bag;
      s.leftover = build.leftover;
      for (const seat of seats) {
        seat.rack = s.bag.splice(0, s.config.startingTiles);
        seat.grid = new Map();
        seat.peeksRemaining = s.config.peek.mode === 'limited' ? s.config.peek.budget : 0;
        seat.lockedUntil = 0;
        seat.finishedAt = null;
      }
      // A countdown for a table of people, so everyone starts together. A
      // player on their own starts when they press the button.
      const countdownMs = seats.length > 1 ? s.config.countdownSec * 1000 : 0;
      s.phase = 'playing';
      s.startedAt = now + countdownMs;
      s.lastDrawAt = now + countdownMs;
      s.roundsTaken = 0;
      note(s, { kind: 'started',
        text: `Match on — ${seats.length} seat${seats.length === 1 ? '' : 's'}, ` +
              `${build.sets} set${build.sets === 1 ? '' : 's'}, ` +
              `${roundsRemaining(s)} rounds in the bag` }, now);
      return done({ e: 'started', countdownMs });
    }

    // -----------------------------------------------------------------
    case 'place': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const fromRack = seat.rack.findIndex((t) => t.id === a.tileId);
      const dk = keyOf(a.to);
      const sitting = seat.grid.get(dk);
      if (sitting) {
        // Dropping a tile back where it already was is a no-op, not an error:
        // touch gestures produce these constantly.
        if (sitting.id === a.tileId) return done();
        return fail('That square is taken.');
      }
      let tile: Tile | undefined;
      if (fromRack >= 0) {
        tile = seat.rack.splice(fromRack, 1)[0] as Tile;
      } else {
        for (const [k, t] of seat.grid) {
          if (t.id === a.tileId) { tile = t; seat.grid.delete(k); break; }
        }
      }
      if (!tile) return fail('You do not hold that tile.');
      seat.grid.set(dk, tile);
      seat.lastPlacementAt = now;
      return done({ e: 'placed', seatId: seat.id, tileId: tile.id, to: a.to },
        ...newWordEvents(seat, a.to));
    }

    case 'recall': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      for (const [k, t] of seat.grid) {
        if (t.id === a.tileId) {
          seat.grid.delete(k);
          if (t.isBlank) t.assigned = null;
          seat.rack.push(t);
          seat.lastPlacementAt = now;
          return done({ e: 'recalled', seatId: seat.id, tileId: t.id });
        }
      }
      return fail('That tile is not on your grid.');
    }

    case 'recallAll': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const n = seat.grid.size;
      for (const t of seat.grid.values()) {
        if (t.isBlank) t.assigned = null;
        seat.rack.push(t);
      }
      seat.grid.clear();
      seat.lastPlacementAt = now;
      return done({ e: 'moved', seatId: seat.id, count: n });
    }

    /**
     * Move a whole selection at once, keeping relative offsets. Validated as a
     * batch so a block can slide into space it is itself vacating.
     */
    case 'moveGroup': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const moves = new Map<string, Coord>();
      for (const m of a.moves) moves.set(m.tileId, m.to);
      const owned = new Set([...seat.grid.values()].map((t) => t.id));
      for (const id of moves.keys()) {
        if (!owned.has(id)) return fail('That tile is not on your grid.');
      }
      const check = canPlaceAll(seat.grid, moves);
      if (!check.ok) return fail('Those tiles would land on top of others.');
      seat.grid = applyMoves(seat.grid, moves);
      seat.lastPlacementAt = now;
      return done({ e: 'moved', seatId: seat.id, count: moves.size });
    }

    case 'assignBlank': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const tile = [...seat.grid.values(), ...seat.rack].find((t) => t.id === a.tileId);
      if (!tile) return fail('You do not hold that tile.');
      if (!tile.isBlank) return fail('That tile is not a blank.');
      tile.assigned = a.letter;
      seat.lastPlacementAt = now;
      return done({ e: 'blankAssigned', seatId: seat.id, tileId: tile.id, letter: a.letter });
    }

    case 'reorderRack': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const byId = new Map(seat.rack.map((t) => [t.id, t]));
      const next: Tile[] = [];
      for (const id of a.order) {
        const t = byId.get(id);
        if (t) { next.push(t); byId.delete(id); }
      }
      seat.rack = [...next, ...byId.values()];
      return done();
    }

    /**
     * A whole layout of the seat's own tiles, applied in one step.
     *
     * Every tile named must be one the seat holds, named once, and no two may
     * share a square. A held tile left unnamed goes to the end of the rack
     * rather than failing the whole thing: a draw can land between the client
     * working out the layout and the server applying it, and those new letters
     * belong in the rack anyway.
     */
    case 'arrange': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const held = new Map<string, Tile>();
      const was = new Map<string, CoordKey>();
      for (const t of seat.rack) held.set(t.id, t);
      for (const [k, t] of seat.grid) { held.set(t.id, t); was.set(t.id, k); }
      const named = new Set<string>();
      const grid = new Map<CoordKey, Tile>();
      for (const m of a.grid) {
        const t = held.get(m.tileId);
        if (!t || named.has(m.tileId)) return fail('That layout does not match your tiles.');
        const k = keyOf(m.to);
        if (grid.has(k)) return fail('Two tiles cannot share a square.');
        named.add(m.tileId);
        grid.set(k, t);
      }
      const rack: Tile[] = [];
      for (const id of a.rack) {
        const t = held.get(id);
        if (!t || named.has(id)) return fail('That layout does not match your tiles.');
        named.add(id);
        rack.push(t);
      }
      for (const t of seat.rack) if (!named.has(t.id)) rack.push(t);
      for (const t of seat.grid.values()) if (!named.has(t.id)) rack.push(t);
      // A blank going back to the rack forgets its letter, as a recall does.
      for (const t of rack) if (t.isBlank && was.has(t.id)) t.assigned = null;
      const changed: Coord[] = [];
      for (const [k, t] of grid) if (was.get(t.id) !== k) changed.push(parseKey(k));
      let lifted = 0;
      for (const [id, k] of was) if (grid.get(k)?.id !== id) lifted++;
      seat.grid = grid;
      seat.rack = rack;
      if (changed.length === 0 && lifted === 0) return done();
      seat.lastPlacementAt = now;
      const words = new Map<string, GameEvent>();
      for (const c of changed) {
        for (const ev of newWordEvents(seat, c)) if (ev.e === 'wordFormed') words.set(ev.word, ev);
      }
      return done({ e: 'moved', seatId: seat.id, count: Math.max(changed.length, lifted) },
        ...words.values());
    }

    // -----------------------------------------------------------------
    /**
     * The call. Empty rack plus a legal grid means everyone draws; if the bag
     * cannot serve the whole table, the caller has won — which is exactly the
     * tabletop ending, where the last successful call is the winning one.
     */
    case 'declare': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      if (now < seat.lockedUntil) {
        const secs = Math.ceil((seat.lockedUntil - now) / 1000);
        return fail(`Locked out for another ${secs}s after that last call.`);
      }
      const check = canDeclare(seat.grid, seat.rack.length, dict, {
        minWordLength: s.config.minWordLength, decide: true,
      });
      if (!check.ok) {
        const pen = s.config.peek.penalty;
        let lockedUntil = seat.lockedUntil;
        if (pen === 'cooldown') {
          lockedUntil = now + s.config.peek.cooldownMs;
          seat.lockedUntil = lockedUntil;
        } else if (pen === 'drawTiles') {
          const extra = s.bag.splice(0, s.config.peek.drawPenalty);
          seat.rack.push(...extra);
        }
        note(s, { kind: 'invalidCall', seatId: seat.id,
          text: `${seat.name} called too soon` }, now);
        return {
          ok: false,
          error: check.reason ?? 'Not ready.',
          events: [{ e: 'invalidCall', seatId: seat.id,
            reason: check.reason ?? 'Not ready.', lockedUntil }],
        };
      }

      const need = drawNeeded(s);
      if (s.bag.length < need) {
        // Order matters: the phase and the finish time are what make this seat
        // count as having gone out, and `standings` reads both to award the
        // bonus before it decides who is top.
        seat.finishedAt = now;
        s.phase = 'finished';
        s.finishedAt = now;
        s.wentOutSeatId = seat.id;
        const table = standings(s, dict);
        s.winnerSeatId = table[0]?.seatId ?? seat.id;
        const bonus = s.config.goingOutBonus;
        note(s, { kind: 'finished', seatId: seat.id,
          text: `${seat.name} used every letter — that's the match`
                + (bonus > 0 ? `, and ${bonus} points for going out` : '') }, now);
        return done({ e: 'won', seatId: seat.id, bonus });
      }

      note(s, { kind: 'declare', seatId: seat.id,
        text: `${seat.name} called ${BRAND.callPhraseWords(s.config.drawPerRound)}` +
              ` — everyone draws ${s.config.drawPerRound}` }, now);
      return done(...drawRound(s, 'call', seat.id, now));
    }

    /**
     * Say whether this seat is stuck.
     *
     * Unanimity among the seats still playing releases a draw for everybody.
     * It is always available — no setting turns it off — because a table can
     * genuinely reach a position nobody can play out of, and the alternative
     * is abandoning the match. It costs a round from the bag like any other
     * draw, so it cannot be leaned on indefinitely.
     */
    case 'stuck': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      if (seat.stuck === a.stuck) return done();
      seat.stuck = a.stuck;
      const { count, needed } = stuckTally(s);
      const events: GameEvent[] = [
        { e: 'stuckChanged', seatId: seat.id, stuck: a.stuck, count, needed },
      ];
      if (count < needed || needed === 0) {
        note(s, { kind: 'stuck', seatId: seat.id,
          text: a.stuck ? `${seat.name} is stuck (${count} of ${needed})`
                        : `${seat.name} is unstuck (${count} of ${needed})` }, now);
        return done(...events);
      }
      // Everybody agrees. If the bag cannot serve the table, there is nothing
      // left to rescue anyone with and the match is genuinely over.
      if (s.bag.length < drawNeeded(s)) {
        s.phase = 'finished';
        s.finishedAt = now;
        s.winnerSeatId = standings(s, dict)[0]?.seatId ?? null;
        note(s, { kind: 'stalemate',
          text: 'Everyone is stuck and the bag is empty — match over' }, now);
        return done(...events, { e: 'ended', reason: 'everyone stuck, no tiles left' });
      }
      note(s, { kind: 'stuck',
        text: `Everyone is stuck — the table draws ${s.config.drawPerRound}` }, now);
      return done(...events, ...drawRound(s, 'consensus', null, now));
    }

    case 'dump': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      if (!s.config.dumpEnabled) return fail('Trading letters in is switched off for this match.');
      const idx = seat.rack.findIndex((t) => t.id === a.tileId);
      if (idx < 0) return fail('That tile is not in your hand.');
      if (s.bag.length < s.config.dumpRatio) return fail('Not enough tiles left to dump.');
      const [given] = seat.rack.splice(idx, 1);
      const got = s.bag.splice(0, s.config.dumpRatio);
      seat.rack.push(...got);
      if (given) { if (given.isBlank) given.assigned = null; s.bag.push(given); }
      note(s, { kind: 'dump', seatId: seat.id,
        text: `${seat.name} traded a letter in for ${s.config.dumpRatio}` }, now);
      return done({ e: 'dumped', seatId: seat.id, gave: given?.id ?? '',
        got: got.map((t) => t.id) });
    }

    /**
     * Spend one check. The window matters: a check is only on offer while the
     * grid is freshly changed, so it rewards "I think this is right, confirm
     * it" and gives nothing to someone cycling letters hunting for a hit.
     */
    case 'peek': {
      const seat = playable(s, a.seatId, now);
      if (typeof seat === 'string') return fail(seat);
      const policy = s.config.peek;
      if (policy.mode === 'live') return done({ e: 'peeked', seatId: seat.id, remaining: Infinity });
      if (policy.mode === 'off') return fail('Word checking is switched off for this match.');
      if (seat.peeksRemaining <= 0) return fail('No checks left.');
      if (policy.windowMs > 0 && now - seat.lastPlacementAt > policy.windowMs) {
        const secs = Math.round(policy.windowMs / 1000);
        return fail(`Checks expire ${secs}s after you last moved a tile.`);
      }
      seat.peeksRemaining--;
      note(s, { kind: 'peek', seatId: seat.id,
        text: `${seat.name} used a check (${seat.peeksRemaining} left)` }, now);
      return done({ e: 'peeked', seatId: seat.id, remaining: seat.peeksRemaining });
    }

    case 'requestView':
    case 'grantView':
      return done();

    // -----------------------------------------------------------------
    case 'concede': {
      const seat = s.seats.get(a.seatId);
      if (!seat) return fail('Unknown seat.');
      seat.finishedAt = now;
      note(s, { kind: 'left', seatId: seat.id, text: `${seat.name} bowed out` }, now);
      if (activeSeats(s).length === 0) {
        s.phase = 'finished';
        s.finishedAt = now;
        return done({ e: 'ended', reason: 'everyone conceded' });
      }
      return done();
    }

    case 'endMatch': {
      const p = s.players.get(a.playerId);
      if (!p?.isHost) return fail('Only the host can end the match.');
      s.phase = 'finished';
      s.finishedAt = now;
      s.winnerSeatId ??= standings(s, dict)[0]?.seatId ?? null;
      note(s, { kind: 'stalemate', text: `Host ended the match: ${a.reason}` }, now);
      return done({ e: 'ended', reason: a.reason });
    }

    /**
     * Play again with the same people.
     *
     * Everything about the table survives — who is sitting where, which pairs
     * are teamed up, the rules they agreed — and only the match itself is
     * thrown away. Without this the only way to have another game is for
     * everybody to leave and rebuild the table from scratch, which after a
     * good match is a silly thing to ask of people.
     */
    case 'rematch': {
      const p = s.players.get(a.playerId);
      if (!p?.isHost) return fail('Only the host can start another match.');
      if (s.phase !== 'finished') return fail('This match is still going.');
      for (const seat of s.seats.values()) {
        seat.rack = [];
        seat.grid = new Map();
        seat.peeksRemaining = 0;
        seat.lockedUntil = 0;
        seat.lastPlacementAt = 0;
        seat.finishedAt = null;
        seat.stuck = false;
      }
      s.phase = 'lobby';
      s.bag = [];
      s.leftover = [];
      s.roundsTaken = 0;
      s.startedAt = null;
      s.lastDrawAt = 0;
      s.finishedAt = null;
      s.winnerSeatId = null;
      s.wentOutSeatId = null;
      // A fresh shuffle, or the rematch deals the identical bag.
      s.config = { ...s.config, seed: randomSeed() };
      s.log = [];
      note(s, { kind: 'config', playerId: p.id, text: `${p.name} set up another match` }, now);
      return done({ e: 'rematch' });
    }

    case 'tick': {
      if (s.phase !== 'playing') return done();
      // The clock forces a draw before it considers ending the match, so a
      // table that is merely slow gets rescued rather than guillotined.
      if (s.config.forceDrawMs > 0 && now - s.lastDrawAt >= s.config.forceDrawMs
          && s.bag.length >= drawNeeded(s) && drawNeeded(s) > 0) {
        note(s, { kind: 'declare',
          text: `Time is up for this round — everyone draws ${s.config.drawPerRound}` }, now);
        return done(...drawRound(s, 'timer', null, now));
      }
      if (s.config.timeLimitMs > 0 && s.startedAt !== null &&
          now - s.startedAt >= s.config.timeLimitMs) {
        s.phase = 'finished';
        s.finishedAt = now;
        s.winnerSeatId = standings(s, dict)[0]?.seatId ?? null;
        note(s, { kind: 'stalemate', text: 'Time is up' }, now);
        return done({ e: 'ended', reason: 'time limit reached' });
      }
      return done();
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function playable(s: MatchState, seatId: string, now?: number): Seat | string {
  if (s.phase !== 'playing') return 'The match is not running.';
  if (now !== undefined && s.startedAt !== null && now < s.startedAt) return 'Not yet — the countdown is still running.';
  const seat = s.seats.get(seatId);
  if (!seat) return 'Unknown seat.';
  if (seat.finishedAt !== null) return 'This seat has already finished.';
  return seat;
}

function detach(s: MatchState, p: Player): void {
  const old = s.seats.get(p.seatId);
  if (!old) return;
  old.members = old.members.filter((m) => m !== p.id);
  if (old.members.length === 0) s.seats.delete(old.id);
}

/** How long a host may be unreachable before somebody else can take over. */
export const HOST_GRACE_MS = 2 * 60 * 1000;

/** Take a player off the table entirely: lobby only. */
function dropPlayer(s: MatchState, p: Player): void {
  const seat = s.seats.get(p.seatId);
  if (seat) {
    seat.members = seat.members.filter((m) => m !== p.id);
    if (seat.members.length === 0) s.seats.delete(seat.id);
  }
  s.players.delete(p.id);
  if (p.isHost) promoteHost(s);
}

/**
 * A host who has been unreachable for longer than the grace period gives the
 * role up — to `claimant` if one is named and present, otherwise to the first
 * player who is. Without this a table whose host's phone died could never
 * start.
 */
function reclaimHostIfAbandoned(s: MatchState, now: number, claimant?: string): void {
  const host = [...s.players.values()].find((p) => p.isHost);
  if (!host || host.connected) return;
  if (now - (host.disconnectedAt ?? now) < HOST_GRACE_MS) return;
  const next = (claimant && s.players.get(claimant)?.connected)
    ? s.players.get(claimant)
    : [...s.players.values()].find((p) => p.connected);
  if (!next) return;
  host.isHost = false;
  next.isHost = true;
}

function promoteHost(s: MatchState): void {
  const next = [...s.players.values()].find((p) => p.connected);
  if (next) (s.players.get(next.id) as Player).isHost = true;
}

function recount(s: MatchState): void {
  for (const seat of s.seats.values()) {
    seat.connectedMembers = seat.members
      .filter((m) => s.players.get(m)?.connected).length;
  }
}

/** Runs that pass through a just-placed cell, for the "word formed" chime. */
function newWordEvents(seat: Seat, at: Coord): GameEvent[] {
  const k = keyOf(at);
  return extractRuns(seat.grid)
    .filter((r) => r.cells.some((c) => keyOf(c) === k) && !r.word.includes('?'))
    .map((r) => ({ e: 'wordFormed' as const, seatId: seat.id, word: r.word }));
}

/**
 * Reject settings the tile supply or the rules cannot honour.
 *
 * Every numeric bound is an explicit integer check rather than a pair of
 * comparisons, because `NaN < 2` and `NaN > 40` are both false — so a
 * comparison-only guard waves NaN, Infinity and fractions straight through to
 * the bag arithmetic, which then produces a match nobody can play. This runs
 * on the server against whatever a client actually sent.
 */
const counted = (n: number, lo: number, hi: number): boolean =>
  Number.isInteger(n) && n >= lo && n <= hi;

export function checkConfig(c: MatchConfig, seatCount: number): string | null {
  if (!counted(c.startingTiles, 2, 40)) return 'Starting tiles must be a whole number, 2-40.';
  if (!counted(c.rounds, 0, 60)) return 'Rounds must be a whole number, 0-60.';
  if (!counted(c.drawPerRound, 1, 10)) return 'Draw per round must be a whole number, 1-10.';
  if (!counted(c.minWordLength, 2, 5)) return 'Minimum word length must be a whole number, 2-5.';
  if (!counted(c.dumpRatio, 1, 5)) return 'Dump ratio must be a whole number, 1-5.';
  if (!counted(c.goingOutBonus, 0, 200)) return 'Going-out bonus must be a whole number, 0-200.';
  if (!counted(c.countdownSec, 0, 10)) return 'Countdown must be a whole number of seconds, 0-10.';
  if (!['both', 'british', 'american'].includes(c.spelling)) return 'Spelling must be British, American or both.';
  if (!counted(c.timeLimitMs, 0, 24 * 60 * 60 * 1000)) return 'Time limit must be 0-24 hours.';
  if (!counted(c.forceDrawMs, 0, 60 * 60 * 1000)) return 'Forced draw must be 0-60 minutes.';
  if (!counted(c.peek.budget, 0, 99)) return 'Check budget must be a whole number, 0-99.';
  if (!counted(c.peek.budgetPerRound, 0, 99)) return 'Checks per round must be a whole number, 0-99.';
  if (!counted(c.peek.windowMs, 0, 60 * 60 * 1000)) return 'Check window must be 0-60 minutes.';
  if (!counted(c.peek.cooldownMs, 0, 60 * 60 * 1000)) return 'Lockout must be 0-60 minutes.';
  if (!counted(c.peek.drawPenalty, 0, 20)) return 'Draw penalty must be a whole number, 0-20.';
  const sets = requiredSets(seatCount, c);
  if (sets > 8) return `That would need ${sets} tile sets — try fewer rounds or tiles.`;
  const got = roundsAvailable(seatCount, c.startingTiles, c.drawPerRound, sets);
  if (got < c.rounds) return `Only ${got} rounds fit; reduce rounds or starting tiles.`;
  return null;
}

export interface Standing {
  seatId: string;
  name: string;
  tilesPlaced: number;
  tilesInHand: number;
  /** The total from {@link ScoreBreakdown}: words, less unused, plus bonus. */
  score: number;
  /** Everything that total is made of, for the end-of-match reveal. */
  breakdown: ScoreBreakdown;
  complete: boolean;
  /** This seat used every letter on a legal grid, so took the bonus. */
  wentOut: boolean;
  finishedAt: number | null;
}

/**
 * True when this seat is the one that emptied its hand and stopped the match.
 *
 * Derived rather than stored: an empty rack over a grid that is entirely
 * words is exactly what going out means, and it is the same test `declare`
 * had to pass to end the match in the first place. Conceding sets
 * `finishedAt` too, but a seat that could have gone out would have called
 * rather than bowed out.
 */
function wentOut(s: MatchState, seat: Seat, complete: boolean): boolean {
  // Recorded at the moment it happened; the inference is only for a match
  // saved by a build from before the field existed.
  if (s.wentOutSeatId !== undefined && s.wentOutSeatId !== null) {
    return s.wentOutSeatId === seat.id;
  }
  return s.phase === 'finished' && seat.finishedAt !== null
    && seat.rack.length === 0 && complete;
}

/**
 * Final ranking, by score.
 *
 * Going out is worth {@link MatchConfig.goingOutBonus} and ends the match, so
 * the player who calls it is nearly always top — they carry no penalty and
 * everyone else is caught holding letters. It is a bonus rather than an
 * outright win because that is what makes the rest of the table's grids worth
 * building: raise the bonus and going out becomes decisive, lower it and a
 * dense grid can still take the match.
 *
 * Ties fall back to the older ordering, so a match played with the bonus at
 * zero and identical scores still ranks sensibly.
 */
export function standings(s: MatchState, dict: Dictionary): Standing[] {
  const rows: Standing[] = [...s.seats.values()].map((seat) => {
    const report = validateGrid(seat.grid, dict, {
      minWordLength: s.config.minWordLength, decide: true,
    });
    const complete = report.complete && seat.rack.length === 0;
    const out = wentOut(s, seat, complete);
    const breakdown = scoreSeat(seat, dict, s.config, { wentOut: out });
    return {
      seatId: seat.id,
      name: seat.name,
      tilesPlaced: seat.grid.size,
      tilesInHand: seat.rack.length,
      score: breakdown.total,
      breakdown,
      complete,
      wentOut: out,
      finishedAt: seat.finishedAt,
    };
  });
  rows.sort((a, b) =>
    b.score - a.score ||
    Number(b.complete) - Number(a.complete) ||
    (a.finishedAt ?? Infinity) - (b.finishedAt ?? Infinity) ||
    b.tilesPlaced - a.tilesPlaced ||
    a.tilesInHand - b.tilesInHand);
  return rows;
}

/**
 * Give any setting a table does not have its default.
 *
 * A table outlives a deploy: one saved before a setting existed carries no
 * value for it at all, and the check on starting then refused the match —
 * a player mid-evening was told "countdown must be 0-10" about a setting
 * nobody had seen. Filled in on every action, so a new setting is always
 * simply its default until somebody changes it.
 */
export function fillMissingSettings(config: MatchConfig): void {
  const c = config as unknown as Record<string, unknown>;
  const d = DEFAULT_CONFIG as unknown as Record<string, unknown>;
  for (const k of Object.keys(d)) {
    if (k === 'peek' || k === 'visibility') continue;
    if (c[k] === undefined || c[k] === null) c[k] = d[k];
  }
  config.peek = { ...DEFAULT_CONFIG.peek, ...(config.peek ?? {}) };
  config.visibility = { ...DEFAULT_CONFIG.visibility, ...(config.visibility ?? {}) };
}

/**
 * Add a finished match to the table's running totals.
 *
 * Every member of a seat is credited with the seat's score, and with the win
 * if the seat won. Names are refreshed each time, so a player who renamed
 * between matches shows under their current name.
 */
function tallySeries(s: MatchState, dict: Dictionary): void {
  const series = s.series ?? (s.series = { games: 0, totals: {} });
  series.games++;
  for (const row of standings(s, dict)) {
    const seat = s.seats.get(row.seatId);
    if (!seat) continue;
    for (const id of seat.members) {
      const t = series.totals[id] ?? (series.totals[id] = { name: '', points: 0, wins: 0, games: 0 });
      t.name = s.players.get(id)?.name ?? seat.name;
      t.points += row.score;
      t.games++;
      if (s.winnerSeatId === row.seatId) t.wins++;
    }
  }
}

/** Deep-enough copy for optimistic prediction and undo snapshots. */
export function cloneSeat(seat: Seat): Seat {
  return {
    ...seat,
    members: [...seat.members],
    rack: seat.rack.map((t) => ({ ...t })),
    grid: new Map([...seat.grid].map(([k, t]) => [k, { ...t }])),
  };
}

export { faceOf, key };
